import React, { useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import './Calculator.scss';
import { RootState } from 'src/configs/store';
import Icon from 'src/components/Icon/Icon';

interface CalculatorProps {
  onClose: Function
}

function Calculator({ onClose }: CalculatorProps) {
  const [toGetValue, setToGet] = useState('');
  const [received, setReceived] = useState('');
  const [operation, setOperation] = useState('');
  const [hasResulted, setHasResulted] = useState(false);

  const { toGet } = useSelector((state:RootState) => state.peripherals.payments);

  const resultCalc = () => {
    switch (operation) {
      case '+':
        setToGet(((+toGetValue) + (+received)).toFixed(2).toString());
        setReceived('');
        break;
      case 'X':
        setToGet(((+toGetValue) * (+received)).toFixed(2).toString());
        setReceived('');
        break;
      case '-':
        setToGet(((+toGetValue) - (+received)).toFixed(2).toString());
        setReceived('');
        break;
      case '/':
        setToGet(((+toGetValue) / (+received)).toFixed(2).toString());
        setReceived('');
        break;
      default:
        setToGet('');
        setReceived('');
        break;
    }
    setOperation('');
  };

  const inputRef = useRef(null);

  const checkEnter = (e) => {

    if (e.key === 'Tab'){
      if (inputRef.current.value === '' || hasResulted){
        e.preventDefault();
        return ;
      }
    }

    if (e.key === 'Enter'){
      e.preventDefault();
      if (!hasResulted){
        resultCalc();
        setHasResulted(true);
      } else {
        setToGet('');
        setReceived('');
        setOperation('');
        setHasResulted(false);
      }
    } else if (e.key === '+' || e.key === '-' || e.key === '/'){
      e.preventDefault();
      if (inputRef.current.value === '' || hasResulted){
        e.preventDefault();
        return ;
      }
      setOperation(e.key);
      inputRef.current.value='';
      inputRef.current.focus();
    } else if (e.keyCode === 187 || e.key === '*'){
      e.preventDefault();
      if (inputRef.current.value === '' || hasResulted){
        e.preventDefault();
        return ;
      }
      setOperation('X');
      inputRef.current.value='';
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    addEventListener('keydown', checkEnter);

    return () => {
      removeEventListener('keydown', checkEnter);
    };
  }, [toGetValue, received, operation, hasResulted]);

  useEffect(() => {
    setToGet(toGet > 0 ? (toGet / 100) + '' : '');
  }, []);

  const buttons = [
    ['AC', '+/-', '%', '/'],
    ['7', '8', '9', 'X'],
    ['4', '5', '6', '-'],
    ['1', '2', '3', '+'],
    ['0', '.', '=']
  ];

  const handleRef = (e) => {
    e.preventDefault();
    inputRef.current = e.target;
  };

  const handleChange = (e) => {
    if (operation === ''){
      setToGet(e.target.name ? e.target.value : toGetValue + e.target.value);
    } else {
      setReceived(e.target.name ? e.target.value : received + e.target.value);
    }
  };

  const checkValue = () => {
    if (operation === '' || received === ''){
      return toGetValue;
    }
    return received;
  };

  const handleFunction = (e) => {
    if (e.target.value === '='){
      resultCalc();
      setHasResulted(true);
    } else if (e.target.value === 'AC'){
      setToGet('');
      setReceived('');
      setOperation('');
      setHasResulted(false);
    } else if (e.target.value ==='.'){
      if (operation === ''){
        setToGet(toGetValue + '.');
      } else {
        setReceived(received + '.');
      }
    } else if (e.target.value === '+/-') {
      if (operation === ''){
        setToGet(toGetValue.includes('-') ? toGetValue.replace('-', '') : '-' + toGetValue);
      } else {
        setReceived(received.includes('-') ? received.replace('-', '') : '-' + received);
      }
    } else {
      if (inputRef.current.value === '' || hasResulted){
        e.preventDefault();
        return ;
      }
      setOperation(e.target.value);
    }
  };

  return (
    <div className="calculator">
      <Icon icon="close" className="calculator__close" onClick={ onClose } />
      <div className="calculator__inputs-container">
        <input
          type="text"
          onChange={ handleChange }
          autoFocus
          value={ checkValue() }
          name="toGetValue"
          className="calculator__toget"
          onFocus={ handleRef }
        />
      </div>
      <div className="calculator__buttons-container">
        {
          _.map(buttons, (row, index) => {
            return (
              <div className="calculator__row" key={ index }>
                {
                  _.map(row, (button, i) => {
                    return (
                      <button
                        key={ i }
                        onClick={ _.isNaN(+button) ? handleFunction : handleChange }
                        value={ button }
                        className={
                          classNames({
                            'calculator__button': true,
                            'calculator__button--big': button === '0',
                            'calculator__button--function': _.isNaN(+button),
                            'calculator__button--selected': operation === button,
                            'calculator__button--equal': button === '=',
                            'modal-submit': (button === '=' && !hasResulted) || (button === 'AC' && hasResulted)
                          })
                        }
                        type="button"
                      >{ button } </button>
                    );
                  })
                }
              </div>
            );
          })
        }
      </div>
    </div>
  );
}

export default Calculator;
